import axios from "axios";
import * as yup from "yup";
import { useFormik } from "formik";
import React, { useState } from "react";

import {
  Grid,
  Button,
  Dialog,
  Select,
  MenuItem,
  TextField,
  InputLabel,
  DialogTitle,
  FormControl,
  DialogActions,
  DialogContent,
  FormHelperText,
} from "@material-ui/core";

import Loader from "../components/Loader";

const PROTOCOLS = {
  TCP: "TCP",
  UDP: "UDP",
  BOTH: "BOTH",
};

export default function DevicePortForwardingForm({
  initialData,
  deviceSerial,
  setMessage,
  setError,
  open,
  handleOpen,
  availableIps,
  callRules,
  callback,
}) {
  const [loading, setLoading] = useState(false);

  const validateIPAddress = (address) => {
    if (
      /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/.test(
        address
      )
    ) {
      return true;
    }

    return false;
  };

  const validationSchema = yup.object({
    inbound_ip: yup
      .string("Inbound IP")
      .required("Inbound IP is required")
      .test("validate-ip", "Invalid IP address", validateIPAddress),
    inbound_port: yup
      .number("Inbound Port")
      .min(1, "Provide valid port")
      .max(65000, "Provide valid port")
      .required("Inbound Port is required"),
    internal_ip: yup
      .string("Internal IP")
      .required("Internal IP is required")
      .test("validate-ip", "Invalid IP address", validateIPAddress),
    internal_port: yup
      .number("Inbound Port")
      .min(1, "Provide valid port")
      .max(65000, "Provide valid port")
      .required("Internal Port is required"),
    protocol: yup.string("Protocol").required("Protocol is required"),
  });

  const createNewRule = (requestData) => {
    setLoading(true);

    axios
      .post(`/device/port_forward/create_rule/${deviceSerial}`, requestData)
      .then((response) => {
        setMessage("Created new rule successfully.");
      })
      .catch((error) => {
        setError(error?.response?.data?.detail || "Error Creating new rule");
      })
      .finally(() => {
        callRules(deviceSerial);
        formik.setSubmitting(false);
        handleOpen(null);
        callback && callback();
      });
  };

  const formik = useFormik({
    initialValues: initialData || {},
    validationSchema: validationSchema,
    validateOnChange: true,
    onSubmit: (values) => {
      const requestData = Object.keys(values).reduce((obj, key) => {
        if (
          Object.keys(validationSchema.fields).includes(key) &&
          values[key] !== null
        ) {
          obj[key] = values[key];
        }

        return obj;
      }, {});

      if (requestData.protocol === PROTOCOLS.TCP) {
        if (requestData.internal_port === 80)
          requestData.application_protocol = "HTTP";
        if (requestData.internal_port === 443)
          requestData.application_protocol = "HTTPS";
      }

      if (!requestData?.application_protocol)
        requestData.application_protocol = "OTHER";

      createNewRule(requestData);
    },
  });

  if (loading) {
    return <Loader />;
  } else {
    return (
      <React.Fragment>
        <Dialog
          open={open}
          onClose={() => handleOpen(null)}
          aria-labelledby="form-dialog-title"
          maxWidth="sm"
        >
          <DialogTitle id="form-dialog-title">
            Create new Port Forwarding Rule
          </DialogTitle>
          <DialogContent>
            <Grid container direction="column" spacing={2}>
              <Grid item xs={12} sm={12}>
                <FormControl
                  error={Boolean(formik.errors.inbound_ip)}
                  sx={{ width: "100%" }}
                  style={{ minWidth: 180 }}
                >
                  <InputLabel id="inbound_ip">Inbound IP</InputLabel>
                  <Select
                    labelId="inbound_ip"
                    id="inbound_ip"
                    value={formik.values.inbound_ip}
                    placeholder="Inbound IP"
                    onChange={(e) =>
                      formik.setFieldValue("inbound_ip", e.target.value)
                    }
                  >
                    {availableIps.map((ip) => (
                      <MenuItem value={ip} key={ip}>
                        {ip}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.inbound_ip) && (
                    <FormHelperText id="inbound_ip-error-text">
                      {formik.errors.inbound_ip}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl
                  error={Boolean(formik.errors.inbound_port)}
                  sx={{ width: "100%" }}
                >
                  <TextField
                    fullWidth
                    margin="dense"
                    type="number"
                    id="inbound_port"
                    label="Inbound Port"
                    onChange={formik.handleChange}
                    placeholder="Inbound Port"
                    value={formik.inbound_port}
                  />
                  {Boolean(formik.errors.inbound_port) && (
                    <FormHelperText id="inbound_port-error-text">
                      {formik.errors.inbound_port}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl
                  error={Boolean(formik.errors.internal_ip)}
                  sx={{ width: "100%" }}
                >
                  <TextField
                    fullWidth
                    margin="dense"
                    id="internal_ip"
                    label="Internal IP"
                    onChange={formik.handleChange}
                    placeholder="Internal IP"
                    value={formik.internal_ip}
                  />
                  {Boolean(formik.errors.internal_ip) && (
                    <FormHelperText id="internal_ip-error-text">
                      {formik.errors.internal_ip}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl
                  error={Boolean(formik.errors.internal_port)}
                  sx={{ width: "100%" }}
                >
                  <TextField
                    fullWidth
                    margin="dense"
                    type="number"
                    id="internal_port"
                    label="Internal Port"
                    onChange={formik.handleChange}
                    placeholder="Internal Port"
                    value={formik.internal_port}
                  />
                  {Boolean(formik.errors.internal_port) && (
                    <FormHelperText id="internal_port-error-text">
                      {formik.errors.internal_port}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={12}>
                <FormControl
                  error={Boolean(formik.errors.protocol)}
                  sx={{ width: "100%" }}
                  style={{ minWidth: 180 }}
                >
                  <InputLabel id="protocol">Protocol</InputLabel>
                  <Select
                    labelId="protocol"
                    id="protocol"
                    value={formik.values.protocol}
                    placeholder="Protocol"
                    onChange={(e) =>
                      formik.setFieldValue("protocol", e.target.value)
                    }
                  >
                    {Object.keys(PROTOCOLS).map((protocol) => (
                      <MenuItem value={protocol} key={protocol}>
                        {protocol}
                      </MenuItem>
                    ))}
                  </Select>
                  {Boolean(formik.errors.protocol) && (
                    <FormHelperText id="protocol-error-text">
                      {formik.errors.protocol}
                    </FormHelperText>
                  )}
                </FormControl>
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button
              disabled={loading || formik.isSubmitting}
              onClick={() => formik.submitForm()}
              variant="contained"
              color="primary"
            >
              Create New Rule
            </Button>
            <Button onClick={() => handleOpen(null)} color="primary">
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </React.Fragment>
    );
  }
}
